// Bottom-sheet venue card shown when a map marker (or a saved-place row) is
// tapped. Portaled to document.body so it sits above the FAB/tab bar. Extracted
// from App.js; used in several places (map tab, saved lists, results).
//
// On the Map, the card doubles as a discovery surface: when onNext/onPrev are
// provided, a horizontal swipe moves venue-to-venue through the set currently
// shown on the map — swipe RIGHT for the next venue, LEFT to go back — without
// closing the card. A one-time nudge tutorial teaches the gesture. The swipe is
// navigation only; saving is still the bookmark button. While nav is enabled the
// hero stops swiping photos (you tap the photo to advance) so the two gestures
// don't collide.
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, MoreVertical, Send, Bookmark, ChevronRight, ChevronLeft, MapPin, MessageCircle, CalendarDays, Moon, Maximize2 } from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { ListPicker } from "./ListPicker";
import { supabase } from "../supabaseClient";
import { FriendAvatar } from "./FriendAvatar";
import { timeAgoShort, FRESH_MS, DUPE_MS } from "../lib/checkins";
import {
  VenueHeroCarousel,
  venueHasHero,
  BeenPill,
  VenueRating,
  VenueEditorial,
  VenueVibes,
  VenueAmenities,
  VenueReview,
  OpeningHours,
  OpenMapsButton,
} from "./VenueBits";
import { getMapsUrl } from "../lib/venueLogic";
import { useVenueDetails } from "../lib/venueDetails";
import { sendPush } from "../lib/push";
import { markBeen, unmarkBeen, countVisits } from "../lib/been";

const HINT_KEY = "flanit_mapcard_swipe_hint"; // localStorage seen-flag

// Olive dot pin for the card's mini/full map — matches the map tab's look
// without dragging the cluster machinery in here.
function miniPinIcon() {
  return L.divIcon({
    className: "",
    html: '<div style="width:18px;height:18px;border-radius:50%;background:#455d3b;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.35)"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

export function MapVenueSheet({
  venue: venueLight,
  onClose,
  savedIds,
  onSave,
  onUnsave,
  onHide,
  onCheckIn,
  onOpenThread,
  onOpenProfile,
  userId,
  onNext,
  onPrev,
  hasNext = false,
  hasPrev = false,
  // Raise above the check-in card (z-3600) when opened FROM one, so closing
  // this returns you to the check-in rather than losing it.
  zIndex = 3100,
  // Oct 9 (Mark): every card carries a mini map EXCEPT when opened from
  // the Map tab itself (the map is already behind it).
  hideMap = false,
}) {
  // Hydrate the heavy tail (photos/reviews/editorial) on card open — the
  // bootstrap ships light columns only. All body code below uses `venue`
  // exactly as before.
  const venue = useVenueDetails(venueLight);

  // BEEN PILL (July 31) — self-fetched from userId + venue.id, so all seven
  // mounts of this sheet get it with no prop changes (the mount-parity lesson,
  // applied in advance for once). visits = my check-ins here (events, can't be
  // un-happened); marked = the dateless "I've been here" flag. Untick only
  // exists while the mark is the only source.
  const [beenMarked, setBeenMarked] = useState(false);
  const [beenVisits, setBeenVisits] = useState(0);
  useEffect(() => {
    if (!userId || !venue?.id) return;
    let cancelled = false;
    (async () => {
      const [markRes, visitRes] = await Promise.all([
        supabase
          .from("been_marks")
          .select("venue_id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("venue_id", venue.id),
        // Rows, deduped to NIGHTS by countVisits — a mutual tag-accept leaves
        // two shards at one venue for one morning, and that's one visit.
        supabase
          .from("activities")
          .select("venue_id, created_at")
          .eq("user_id", userId)
          .eq("venue_id", venue.id)
          .eq("kind", "checkin"),
      ]);
      if (cancelled) return;
      setBeenMarked((markRes.count ?? 0) > 0);
      setBeenVisits(countVisits(visitRes.data).get(venue.id) || 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, venue?.id]);

  async function toggleBeen() {
    if (!userId || !venue?.id) return;
    if (beenVisits > 0) return; // been from real check-ins — nothing to untick
    if (beenMarked) {
      setBeenMarked(false);
      if (!(await unmarkBeen(userId, venue.id))) setBeenMarked(true);
    } else {
      setBeenMarked(true);
      if (!(await markBeen(userId, venue.id))) setBeenMarked(false);
    }
  }

  const [mapMenuOpen, setMapMenuOpen] = useState(false);
  // The three-tense chooser behind the Plan pill (Oct 9).
  const [planOpen, setPlanOpen] = useState(false);
  // Full-screen map over the card (Oct 9).
  const [mapFull, setMapFull] = useState(false);
  // THE BOOKMARK GRAMMAR (Oct 12, Mark's rulings): tap-empty = instant
  // save + snackbar with "Add to a list"; HOLD = the picker from any
  // state (Instagram muscle memory); tap-filled = the manage sheet
  // (Saved is the top toggle — unticking it IS the unsave). The icon
  // fills when the venue is in ANYTHING (Saved or any writable list).
  const [listPicker, setListPicker] = useState(false);
  const [listCount, setListCount] = useState(0); // memberships in MY lists
  const [savedSnack, setSavedSnack] = useState(false);
  const holdRef = useRef({ timer: null, fired: false });
  useEffect(() => {
    setListCount(0);
    setSavedSnack(false);
    if (!userId || !venue?.id) return;
    let cancelled = false;
    (async () => {
      const [{ data: owned }, { data: mem }] = await Promise.all([
        supabase.from("venue_lists").select("id").eq("owner_id", userId),
        supabase
          .from("venue_list_members")
          .select("list_id")
          .eq("user_id", userId),
      ]);
      const myIds = [
        ...(owned || []).map((l) => l.id),
        ...(mem || []).map((m) => m.list_id),
      ];
      if (myIds.length === 0 || cancelled) return;
      const { data: items } = await supabase
        .from("venue_list_items")
        .select("list_id")
        .eq("venue_id", venue.id)
        .in("list_id", myIds);
      if (!cancelled) setListCount((items || []).length);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, venue?.id]);
  useEffect(() => {
    if (!savedSnack) return;
    const t = setTimeout(() => setSavedSnack(false), 4000);
    return () => clearTimeout(t);
  }, [savedSnack]);
  // TDZ note: `saved` is declared BELOW (near the other derived consts) —
  // reading it here white-screened every card (Oct 12 field find, the
  // repo's classic). savedIds is a prop, safe to read now.
  const bookmarkFilled =
    !!(savedIds && savedIds.has(venue.id)) || listCount > 0;
  function bookmarkTap() {
    if (holdRef.current.fired) {
      holdRef.current.fired = false;
      return; // the hold already opened the picker
    }
    if (!bookmarkFilled) {
      onSave(venue.id);
      setSavedSnack(true);
    } else {
      setListPicker(true); // manage, incl. unsave
    }
  }
  function bookmarkHoldStart() {
    holdRef.current.fired = false;
    holdRef.current.timer = setTimeout(() => {
      holdRef.current.fired = true;
      setListPicker(true);
    }, 450);
  }
  function bookmarkHoldEnd() {
    clearTimeout(holdRef.current.timer);
  }
  const [copied, setCopied] = useState(false);
  const [checkedIn, setCheckedIn] = useState(null); // own activity row after check-in
  // (The post-check-in "what's on" + tagging UI lives in CheckinSheet now —
  // App opens it over this card on a fresh check-in. Mark: embedding it here
  // felt too glued to the venue card.)
  // Friends' check-ins at this venue → the strip. null = loading/none yet.
  // { live: entry|null, liveCount, commentCount, memory: profiles[], memoryCount }
  const [strip, setStrip] = useState(null);
  const [enterDir, setEnterDir] = useState(null); // slide-in dir on venue change
  const [hint, setHint] = useState(false); // show the one-time two-way swipe pill
  const [dragX, setDragX] = useState(0); // live finger-follow offset
  const [animateBack, setAnimateBack] = useState(false); // snap-back transition
  const saved = !!(savedIds && savedIds.has(venue.id));
  const navEnabled = !!(onNext || onPrev);
  const touch = useRef({ x: 0, y: 0, active: false, axis: null });

  // One-time swipe tutorial: show the hint on the very first card the user opens
  // (persisted via localStorage so it only ever shows once).
  useEffect(() => {
    if (!navEnabled || !hasNext) return;
    let seen = false;
    try {
      seen = !!localStorage.getItem(HINT_KEY);
    } catch {
      /* storage blocked — just skip the hint */
    }
    if (!seen) setHint(true);
  }, [navEnabled, hasNext]);

  // Fresh venue on the card → fresh check-in state (swiping venue-to-venue
  // must not carry the "Checked in ✓" pill across).
  useEffect(() => {
    setCheckedIn(null);
    // Pre-flip the pill if you're ALREADY checked in here (recent window) —
    // otherwise a fresh card-open shows "Check in" at a venue you're standing
    // in, which lies. Second tap still opens your thread as usual.
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const since = new Date(Date.now() - DUPE_MS).toISOString();
      const { data } = await supabase
        .from("activities")
        .select("id, created_at, label")
        .eq("user_id", userId)
        .eq("venue_id", venue.id)
        .eq("kind", "checkin")
        .gte("created_at", since)
        .limit(1);
      if (!cancelled && data && data.length > 0) setCheckedIn(data[0]);
    })();
    return () => {
      cancelled = true;
    };
  }, [venue.id, userId]);

  // Friends' check-ins at this venue. RLS scopes the read to own + friends'
  // rows, so the only client work is excluding self and splitting live (<3h,
  // green "is here" strip) from memory (gray "N friends have been here").
  useEffect(() => {
    let cancelled = false;
    setStrip(null);
    if (!userId) return;
    (async () => {
      const { data: rows } = await supabase
        .from("activities")
        .select("id, user_id, created_at, label, show_live")
        .eq("kind", "checkin")
        .eq("venue_id", venue.id)
        .order("created_at", { ascending: false })
        .limit(50);
      // YOU cluster with your friends here — "You and Mark are here", not a
      // strip that pretends you're elsewhere.
      // Stable order: created_at desc, id ASC on ties — a tag-accept is
      // backdated to the ORIGINAL's exact timestamp, and without the tie-break
      // the bare backdated row could win and the thread "loses" its
      // comments/photos (Mark hit this July 17).
      // FUTURE PLANS ARE NOT PRESENCE (Oct 16, Mark: "this future event is
      // saying that we are here now") — a decided plan's created_at sits in
      // the future, and now-minus-future is negative, which sailed under
      // the freshness window. Future rows leave this strip entirely: they
      // are neither "here now" nor "have been here".
      const nowMs = Date.now();
      const all = (rows || [])
        .filter((r) => new Date(r.created_at).getTime() <= nowMs)
        .sort(
          (a, b) =>
            new Date(b.created_at) - new Date(a.created_at) || a.id - b.id
        );
      if (all.length === 0) {
        if (!cancelled) setStrip({ none: true });
        return;
      }
      // Presence is the TOGGLE, not the timestamp (Aug, Mark's unified
      // form): quiet check-ins never make the live "is here" state. They
      // still count toward memory ("N friends have been here") below.
      const fresh = all.filter(
        (r) =>
          r.show_live !== false &&
          nowMs - new Date(r.created_at).getTime() < FRESH_MS
      );
      // Distinct people over all time (memory), newest first, self included.
      const seen = new Set();
      const distinct = [];
      for (const r of all) {
        if (seen.has(r.user_id)) continue;
        seen.add(r.user_id);
        distinct.push(r);
      }
      const needIds = Array.from(
        new Set([...fresh.slice(0, 4), ...distinct.slice(0, 4)].map((r) => r.user_id))
      );
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, display_name, username, avatar_url")
        .in("id", needIds);
      const profById = Object.fromEntries((profs || []).map((p) => [p.id, p]));
      let commentCount = 0;
      // Prefer YOUR OWN check-in as the thread target when you're in the
      // cluster — it's the card with your camera tile and your conversation.
      const ownFresh = fresh.find((r) => r.user_id === userId);
      const target = ownFresh || fresh[0] || distinct[0];
      if (target) {
        const { count } = await supabase
          .from("activity_comments")
          .select("id", { count: "exact", head: true })
          .eq("activity_id", target.id);
        commentCount = count ?? 0;
      }
      if (cancelled) return;
      const freshUsers = Array.from(new Set(fresh.map((r) => r.user_id)));
      const selfFresh = freshUsers.includes(userId);
      const firstOtherId = freshUsers.find((id) => id !== userId) || null;
      setStrip({
        live: fresh[0] ? { ...fresh[0], profile: profById[fresh[0].user_id] } : null,
        liveCount: freshUsers.length,
        selfFresh,
        otherProfile: firstOtherId ? profById[firstOtherId] || null : null,
        selfProfile: profById[userId] || null,
        memory: distinct.slice(0, 3).map((r) => ({
          ...(profById[r.user_id] || {}),
          isSelf: r.user_id === userId,
        })),
        memoryCount: distinct.length,
        memoryHasSelf: distinct.some((r) => r.user_id === userId),
        target: { ...target, profile: profById[target.user_id] },
        commentCount,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [venue.id, userId]);

  function openStripThread() {
    if (!strip || strip.none || !onOpenThread) return;
    // target (not live): it already prefers YOUR OWN check-in in the cluster,
    // then the freshest with the original-first tie-break.
    const t = strip.target || strip.live;
    if (!t) return;
    const isSelf = t.user_id === userId;
    onOpenThread({
      activityId: t.id,
      ownerId: t.user_id,
      ownerName: isSelf ? "You" : t.profile?.display_name || "A friend",
      ownerProfile: isSelf ? null : t.profile || null,
      venueName: venue.name,
      label: t.label || null,
      venueObj: venue, // enables the Join button (and venue link) in the thread
      timestamp: t.created_at,
    });
  }

  // Avatar taps open the person; the card closes so the profile screen
  // (normal z-stack) isn't buried under this portal.
  function openProfile(uid) {
    if (!uid || !onOpenProfile) return;
    onOpenProfile(uid);
    onClose?.();
  }

  // Retire the tutorial after a few seconds even if they don't swipe.
  useEffect(() => {
    if (!hint) return;
    const t = setTimeout(() => {
      setHint(false);
      try {
        localStorage.setItem(HINT_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [hint]);

  function go(dir) {
    if (dir === "next") {
      if (!hasNext || !onNext) return;
      setEnterDir("right");
      onNext();
    } else {
      if (!hasPrev || !onPrev) return;
      setEnterDir("left");
      onPrev();
    }
    setMapMenuOpen(false);
    // First swipe retires the one-time tutorial.
    if (hint) {
      setHint(false);
      try {
        localStorage.setItem(HINT_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  }

  function handleSwipeStart(e) {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, active: true, axis: null };
    setAnimateBack(false);
  }
  function handleSwipeMove(e) {
    if (!touch.current.active) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    // Lock to an axis after a little movement so vertical scrolling still works.
    if (!touch.current.axis && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      touch.current.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }
    if (touch.current.axis !== "x") return;
    // Resist dragging past the ends of the list.
    let offset = dx;
    if ((dx > 0 && !hasNext) || (dx < 0 && !hasPrev)) offset = dx * 0.25;
    setDragX(offset);
  }
  function handleSwipeEnd() {
    if (!touch.current.active) return;
    const wasX = touch.current.axis === "x";
    const dx = dragX;
    touch.current.active = false;
    touch.current.axis = null;
    if (wasX && Math.abs(dx) > 70) {
      // Commit: swap venue and reset offset instantly (the new card slides in).
      setAnimateBack(false);
      setDragX(0);
      go(dx > 0 ? "next" : "prev"); // swipe right → next, left → back
    } else {
      // Snap back to centre.
      setAnimateBack(true);
      setDragX(0);
    }
  }

  // In-app share (July 24, Mark: "you should be able to share it with your
  // friends on the app"): the Share button opens a friend picker; each send
  // = a venue_shares row + a push; the friend gets an Activity item that
  // opens this card. "Share elsewhere" keeps the external link path.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareFriends, setShareFriends] = useState(null); // null = not loaded
  const [shareSent, setShareSent] = useState({}); // friendId -> "sent"|"error"

  async function openShareSheet() {
    setShareOpen(true);
    if (shareFriends !== null || !userId) return;
    const { data: fr } = await supabase
      .from("friendships")
      .select("requester_id, addressee_id")
      .eq("status", "accepted")
      .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
    const ids = Array.from(
      new Set(
        (fr || []).map((f) =>
          f.requester_id === userId ? f.addressee_id : f.requester_id
        )
      )
    );
    if (ids.length === 0) {
      setShareFriends([]);
      return;
    }
    const { data: profs } = await supabase
      .from("profiles")
      .select("id, display_name, username, avatar_url")
      .in("id", ids)
      .order("display_name", { ascending: true });
    setShareFriends(profs || []);
  }

  async function sendVenueShare(friendId) {
    if (shareSent[friendId] === "sent") return;
    setShareSent((prev) => ({ ...prev, [friendId]: "sending" }));
    const { error } = await supabase.from("venue_shares").insert({
      from_user: userId,
      to_user: friendId,
      venue_id: venue.id,
    });
    if (error) {
      console.error("Venue share failed:", error);
      setShareSent((prev) => ({ ...prev, [friendId]: "error" }));
      return;
    }
    setShareSent((prev) => ({ ...prev, [friendId]: "sent" }));
    sendPush(
      friendId,
      "📍 A spot for you",
      `${venue.name}, shared with you on Flanit`,
      `/?v=${venue.id}`,
      { kind: "venue_share", data: { venueId: venue.id } }
    );
  }

  // Share the public card link (flanit.co/v/<id>) — opens this card with no
  // login. Native share sheet on mobile; copy-to-clipboard fallback elsewhere.
  async function handleShare() {
    const url = `https://flanit.co/v/${venue.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: venue.name, text: `${venue.name} — on Flanit`, url });
      } catch {
        /* user cancelled — ignore */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  }

  const slideClass =
    enterDir === "right"
      ? "flanit-slide-from-right"
      : enterDir === "left"
        ? "flanit-slide-from-left"
        : "";

  // Portaled to document.body so it escapes MapScreen's `fixed inset-0
  // z-[1500]` stacking context. Otherwise its zIndex only competes inside that
  // layer and can never sit above the app-level FAB (z-[3060]). `fixed` keeps
  // the same on-screen placement the old `absolute` had inside the fixed map.
  return createPortal(
    <div
      className="fixed left-0 right-0 mx-auto max-w-sm bg-white rounded-3xl border border-neutral-100 shadow-2xl flex flex-col"
      style={{
        bottom: 80,
        width: "calc(100% - 1.5rem)",
        maxHeight: "calc(100% - 100px)",
        // Above the bell (2950) and FAB (3060) so the open card is top-level;
        // callers opening from a check-in card pass 3700 to stack above it.
        zIndex,
        // Live finger-follow while dragging; left undefined at rest so the
        // one-time nudge animation can drive the transform.
        transform: dragX !== 0 ? `translateX(${dragX}px)` : undefined,
        transition: animateBack ? "transform 0.2s ease-out" : "none",
      }}
      onTouchStart={navEnabled ? handleSwipeStart : undefined}
      onTouchMove={navEnabled ? handleSwipeMove : undefined}
      onTouchEnd={navEnabled ? handleSwipeEnd : undefined}
    >
      {/* X floats OVER the full-bleed hero (Sep 7, Mark: the photo owns the
          card's top now). h-0 = the sticky row takes no layout space, so the
          image starts at the card's very top; the button stays reachable
          while scrolling. */}
      {/* NO padding on this row — with border-box, padding IS height, and
          h-0 must stay truly zero or a white strip appears above the hero
          (Sep 7 field find). The button carries its own margins. */}
      <div className="sticky top-0 z-20 h-0 flex items-start justify-end">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={`mt-3.5 mr-4 flex h-9 w-9 shrink-0 items-center justify-center active:scale-90 transition ${
            venueHasHero(venue)
              ? "text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]"
              : "text-neutral-500"
          }`}
        >
          <X size={22} strokeWidth={2.2} />
        </button>
      </div>

      <div
        key={venue.id}
        className={`flex-1 overflow-y-auto p-4 space-y-3 ${
          venueHasHero(venue) ? "pt-0" : "pt-12"
        } ${slideClass}`}
      >
        <VenueHeroCarousel
          venue={venue}
          className="-mx-4 h-[340px] rounded-t-3xl"
          disableSwipe={navEnabled}
          beenPill={
            userId ? (
              <BeenPill
                been={beenMarked || beenVisits > 0}
                visitCount={beenVisits}
                onToggle={toggleBeen}
              />
            ) : null
          }
        />
        <VenueRating venue={venue} />
        <OpeningHours venue={venue} />
        {strip && !strip.none && strip.live && (
          <div className="w-full flex items-center gap-2.5 rounded-2xl bg-[#edf2eb] border border-[#cdd9c6] px-3 py-2.5">
            <span className="flex shrink-0">
              {strip.selfFresh && strip.selfProfile && (
                <FriendAvatar profile={strip.selfProfile} small />
              )}
              {strip.otherProfile && (
                <button
                  type="button"
                  aria-label="View profile"
                  onClick={() => openProfile(strip.otherProfile.id)}
                  className={`active:scale-95 transition ${strip.selfFresh && strip.selfProfile ? "-ml-2" : ""}`}
                >
                  <FriendAvatar profile={strip.otherProfile} small />
                </button>
              )}
            </span>
            <button
              type="button"
              onClick={openStripThread}
              className="flex-1 min-w-0 flex items-center gap-2 text-left active:scale-[0.99] transition"
            >
              <p className="flex-1 min-w-0 text-sm text-[#2f3f29] truncate">
                <strong className="font-medium">
                  {strip.selfFresh
                    ? strip.liveCount === 1
                      ? "You're"
                      : strip.liveCount === 2
                      ? `You and ${(strip.otherProfile?.display_name || "a friend").split(" ")[0]} are`
                      : `You, ${(strip.otherProfile?.display_name || "a friend").split(" ")[0]} +${strip.liveCount - 2} are`
                    : `${strip.live.profile?.display_name || "A friend"}${
                        strip.liveCount > 1 ? ` +${strip.liveCount - 1}` : ""
                      } is`}
                </strong>{" "}
                here · {timeAgoShort(strip.live.created_at)}
                {strip.live.label ? ` · ${strip.live.label}` : ""}
              </p>
              <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#455d3b]">
                <MessageCircle size={14} />
                {strip.commentCount > 0 ? strip.commentCount : ""}
              </span>
            </button>
          </div>
        )}
        {strip && !strip.none && !strip.live && (
          <div className="w-full flex items-center gap-2.5 rounded-2xl border border-neutral-200 px-3 py-2.5">
            <button
              type="button"
              aria-label="View profile"
              onClick={() =>
                openProfile(strip.memory.find((p) => !p.isSelf)?.id)
              }
              className="flex shrink-0 active:scale-95 transition"
            >
              {strip.memory.map((p, i) => (
                <span key={p.id || i} style={i > 0 ? { marginLeft: -8 } : undefined}>
                  <FriendAvatar profile={p} small />
                </span>
              ))}
            </button>
            <button
              type="button"
              onClick={openStripThread}
              className="flex-1 min-w-0 flex items-center gap-2 text-left active:scale-[0.99] transition"
            >
              <p className="flex-1 min-w-0 text-sm text-neutral-600 truncate">
                {strip.memoryHasSelf
                  ? strip.memoryCount === 1
                    ? "You've been here"
                    : `You and ${strip.memoryCount - 1} friend${strip.memoryCount === 2 ? "" : "s"} have been here`
                  : strip.memoryCount === 1
                  ? `${strip.memory[0]?.display_name || "A friend"} has been here`
                  : `${strip.memoryCount} friends have been here`}
              </p>
              <ChevronRight size={14} className="shrink-0 text-neutral-400" />
            </button>
          </div>
        )}
        <VenueEditorial venue={venue} />
        <p className="text-sm leading-6 text-neutral-500">{venue.address}</p>
        <VenueVibes venue={venue} />
        <VenueAmenities venue={venue} />
        <VenueReview venue={venue} />
        {/* MINI MAP (Oct 9, Mark) — Leaflet + the same free Carto tiles as
            the Map tab, so this costs nothing per load. Non-interactive;
            tapping it opens the full-screen version. Hidden when the card
            was opened FROM the map (hideMap) or coords are missing. */}
        {!hideMap &&
          Number.isFinite(Number(venue.latitude)) &&
          Number.isFinite(Number(venue.longitude)) && (
            <button
              type="button"
              aria-label="Open the map"
              onClick={() => setMapFull(true)}
              className="relative mb-3 block h-32 w-full overflow-hidden rounded-2xl border border-neutral-100 active:scale-[0.99] transition"
            >
              <MapContainer
                center={[Number(venue.latitude), Number(venue.longitude)]}
                zoom={15}
                className="h-full w-full pointer-events-none"
                zoomControl={false}
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                touchZoom={false}
                keyboard={false}
                attributionControl={false}
              >
                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2i1k_1_fe5697f3857f77adc7cfbe45" />
                <Marker
                  position={[Number(venue.latitude), Number(venue.longitude)]}
                  icon={miniPinIcon()}
                />
              </MapContainer>
              <span className="absolute bottom-2 right-2 z-[400] flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-neutral-600 shadow">
                <Maximize2 size={13} />
              </span>
            </button>
          )}
        <OpenMapsButton url={getMapsUrl(venue)} />
      </div>

      {hint && dragX === 0 && (
        <div className="flanit-hint-in pointer-events-none absolute bottom-24 left-1/2 z-20 -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-[13px] font-medium text-white shadow-lg backdrop-blur">
            {hasPrev && <ChevronLeft size={16} className="flanit-arrow-left" />}
            <span>Swipe</span>
            <ChevronRight size={16} className="flanit-arrow-right" />
          </div>
        </div>
      )}

      <div className="p-4 pt-3 border-t border-neutral-100 bg-white rounded-b-3xl">
        {/* Post-save snackbar (Oct 12): the list door for a few seconds,
            then gone — ignoring it costs nothing. */}
        {savedSnack && (
          <div className="mb-2.5 flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-2">
            <span className="flex-1 text-xs text-white">Saved ✓</span>
            <button
              type="button"
              onClick={() => {
                setSavedSnack(false);
                setListPicker(true);
              }}
              className="text-xs font-semibold text-[#9db48f]"
            >
              Add to a list ›
            </button>
          </div>
        )}
        <div className="flex items-center justify-around relative">
          <button
            type="button"
            onClick={() => setMapMenuOpen(true)}
            aria-label="More options"
            className="flex h-11 w-11 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 active:scale-95 transition"
          >
            <MoreVertical size={20} />
          </button>
          {/* PLAN (Oct 9, Mark: "make it a calendar icon with the word
              plan... give all 3 options on the pop up from the bottom").
              The one labeled action: opens the three-tense chooser; each
              row lands in the one form with this venue carried. Already
              checked in → the pill stays the door back into your thread. */}
          {onCheckIn && (
            <button
              type="button"
              onClick={() => {
                if (checkedIn) {
                  onOpenThread?.({
                    activityId: checkedIn.id,
                    // ownerId makes it YOUR card — without it the thread
                    // doesn't know it's yours and hides the camera tile.
                    ownerId: userId,
                    ownerName: "You",
                    venueName: venue.name,
                    label: checkedIn.label || null,
                    venueObj: venue,
                    timestamp: checkedIn.created_at,
                  });
                  return;
                }
                setPlanOpen(true);
              }}
              className={`flex-1 mx-1 flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-medium active:scale-95 transition ${
                checkedIn
                  ? "bg-[#edf2eb] text-[#455d3b]"
                  : "bg-[#455d3b] text-white"
              }`}
            >
              {checkedIn ? <MessageCircle size={15} /> : <CalendarDays size={15} />}
              {checkedIn ? "Checked in ✓" : "Plan"}
            </button>
          )}
          <button
            type="button"
            onClick={userId ? openShareSheet : handleShare}
            aria-label="Share"
            className="relative flex h-11 w-11 items-center justify-center rounded-full text-neutral-600 hover:bg-neutral-100 active:scale-95 transition"
          >
            <Send size={20} />
            {copied && (
              <span className="absolute bottom-full mb-1 whitespace-nowrap rounded-full bg-neutral-900 px-2 py-1 text-[10px] font-medium text-white">
                Link copied
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={bookmarkTap}
            onTouchStart={bookmarkHoldStart}
            onTouchEnd={bookmarkHoldEnd}
            onTouchMove={bookmarkHoldEnd}
            onMouseDown={bookmarkHoldStart}
            onMouseUp={bookmarkHoldEnd}
            onMouseLeave={bookmarkHoldEnd}
            aria-label={bookmarkFilled ? "Manage saves and lists" : "Save"}
            className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-neutral-100 active:scale-95 transition"
          >
            <Bookmark
              size={20}
              fill={bookmarkFilled ? "#455d3b" : "none"}
              className={bookmarkFilled ? "text-[#455d3b]" : "text-neutral-600"}
            />
          </button>
          {mapMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-[3400]"
                onClick={() => setMapMenuOpen(false)}
              />
              <div className="absolute bottom-full left-0 mb-2 bg-white border border-neutral-200 rounded-xl shadow-lg z-[3500] overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    onHide(venue.id);
                    setMapMenuOpen(false);
                    onClose();
                  }}
                  className="block px-5 py-3 text-red-700 font-medium hover:bg-neutral-50 whitespace-nowrap text-left"
                >
                  Don't show this again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {/* In-app share sheet — friends first, external link as the exit. */}
      {shareOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end"
          style={{ zIndex: zIndex + 300 }}
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-h-[60%] rounded-t-2xl bg-white flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <p className="text-sm font-semibold text-neutral-900">
                Share {venue.name}
              </p>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                aria-label="Close"
                className="p-1 text-neutral-400"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-3">
              {shareFriends === null && (
                <p className="py-2 text-xs text-neutral-400">
                  Loading friends…
                </p>
              )}
              {shareFriends !== null && shareFriends.length === 0 && (
                <p className="py-2 text-xs text-neutral-400">
                  No friends on Flanit yet — share the link instead.
                </p>
              )}
              <div className="space-y-1">
                {(shareFriends || []).map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center gap-3 rounded-xl px-1 py-2"
                  >
                    <FriendAvatar profile={f} small />
                    <span className="flex-1 min-w-0 truncate text-sm text-neutral-900">
                      {f.display_name || "Someone"}
                    </span>
                    <button
                      type="button"
                      disabled={shareSent[f.id] === "sending"}
                      onClick={() => sendVenueShare(f.id)}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium active:scale-95 transition disabled:opacity-50 ${
                        shareSent[f.id] === "sent"
                          ? "bg-[#edf2eb] border border-[#cdd9c6] text-[#455d3b]"
                          : "bg-[#455d3b] text-white"
                      }`}
                    >
                      {shareSent[f.id] === "sent"
                        ? "Sent ✓"
                        : shareSent[f.id] === "sending"
                        ? "Sending…"
                        : shareSent[f.id] === "error"
                        ? "Retry"
                        : "Send"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-neutral-100 px-5 py-3">
              <button
                type="button"
                onClick={() => {
                  setShareOpen(false);
                  handleShare();
                }}
                className="w-full rounded-full border border-neutral-200 py-2.5 text-sm font-medium text-neutral-700 active:scale-[0.99] transition"
              >
                Share elsewhere
              </button>
            </div>
          </div>
        </div>
      )}
      {listPicker && (
        <ListPicker
          userId={userId}
          venue={venue}
          saved={saved}
          onSave={onSave}
          onUnsave={onUnsave}
          onClose={() => setListPicker(false)}
          onMembershipChange={(n) => setListCount(n)}
        />
      )}
      {/* FULL-SCREEN MAP (Oct 9) — the mini map expanded; interactive,
          one pin, Open in Maps underneath. */}
      {mapFull && (
        <div className="fixed inset-0 z-[3800] bg-white">
          <MapContainer
            center={[Number(venue.latitude), Number(venue.longitude)]}
            zoom={16}
            className="h-full w-full"
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2i1k_1_fe5697f3857f77adc7cfbe45"
            />
            <Marker
              position={[Number(venue.latitude), Number(venue.longitude)]}
              icon={miniPinIcon()}
            />
          </MapContainer>
          <button
            type="button"
            aria-label="Close map"
            onClick={() => setMapFull(false)}
            className="absolute top-4 right-4 z-[1000] flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-700 shadow-md active:scale-95 transition"
          >
            <X size={18} />
          </button>
          <div className="absolute bottom-6 left-0 right-0 z-[1000] px-5">
            <div className="mx-auto max-w-sm rounded-2xl bg-white p-3 shadow-lg">
              <p className="truncate text-sm font-semibold text-neutral-900">
                {venue.name}
              </p>
              <p className="truncate text-xs text-neutral-500">
                {venue.address}
              </p>
              <a
                href={getMapsUrl(venue)}
                target="_blank"
                rel="noreferrer"
                className="mt-2 block rounded-full border border-[#455d3b] py-2 text-center text-xs font-medium text-[#455d3b]"
              >
                Open in Maps
              </a>
            </div>
          </div>
        </div>
      )}
      {/* THE PLAN CHOOSER (Oct 9) — the venue card's when-sheet: Where is
          already answered, these three rows answer When. All land in the
          one form via onCheckIn's mode arg. */}
      {planOpen && (
        <div className="fixed inset-0 z-[3700]">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setPlanOpen(false)}
            className="absolute inset-0 bg-black/30"
          />
          <div
            className="absolute left-0 right-0 bottom-0 mx-auto max-w-md rounded-t-3xl bg-white px-5 pt-3 shadow-2xl"
            style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-neutral-200" />
            <h2 className="mb-3 text-base font-semibold">When?</h2>
            {[
              {
                key: "now",
                icon: <MapPin size={16} />,
                label: "I'm here now",
                sub: "Friends can see it and join you",
              },
              {
                key: "future",
                icon: <CalendarDays size={16} />,
                label: "Coming up",
                sub: "Make the plan, invite people after",
              },
              {
                key: "past",
                icon: <Moon size={16} />,
                label: "Already happened",
                sub: "Add it, photos and all",
              },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setPlanOpen(false);
                  onCheckIn(venue, null, opt.key);
                }}
                className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-neutral-100 bg-white px-4 py-3 text-left active:scale-[0.99] transition"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f5f1ea] text-neutral-500">
                  {opt.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-neutral-900">
                    {opt.label}
                  </span>
                  <span className="block text-[11px] text-neutral-500">
                    {opt.sub}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
